import { useMemo } from "react";
import Accordion from "react-bootstrap/Accordion";
import { AxnAdminUserPropertyCategory } from "@idw-types/data/reference-data/generic";
import { AxnAdminAvailableUserProperty } from "@idw-types/data/axn-admin-available-user-property";
import { AxnAdminUserRpProperty } from "@idw-types/data/axn-admin-user/axn-admin-user-rp-property";
import { AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBodyDescription } from "@containers/common/content-page-body-description/content-page-body-description";
import { RelyingPartyPermissionContainer } from "./relying-party-permission-container/relying-party-permission-container";
import commonStyles from "../organization-user-details-permissions-panel-tab.less";
import styles from "./relying-party-permissions-container.less";

type RelyingPartyPermissionsContainerProps = {
    isDisabled: boolean;
    canModify: boolean;
    userId: string;
    isExpandedByDefault: boolean;
    relyingParty: AxnRelyingParty;
    axnAdminUserPropertyCategories: Array<AxnAdminUserPropertyCategory>;
    axnAdminUserRpProperties: Array<AxnAdminUserRpProperty>;
    axnAdminAvailableUserRPProperties: Array<AxnAdminAvailableUserProperty>;
    addRelyingPartyProperties: (
        rpProperties: Array<AxnAdminUserRpProperty>,
        relyingParty: AxnRelyingParty
    ) => void;
    removeRelyingPartyProperties: (
        rpProperties: Array<AxnAdminUserRpProperty>,
        relyingParty: AxnRelyingParty
    ) => void;
};

export const RelyingPartyPermissionsContainer = (props: RelyingPartyPermissionsContainerProps) => {
    const {
        isDisabled,
        canModify,
        userId,
        relyingParty,
        isExpandedByDefault,
        axnAdminUserPropertyCategories,
        axnAdminUserRpProperties,
        axnAdminAvailableUserRPProperties,
        addRelyingPartyProperties,
        removeRelyingPartyProperties
    } = props;

    const categorizedRPProperties = useMemo(() => {
        const categoryPropertyArrays = axnAdminUserPropertyCategories.map(category => {
            const categoryProperties = axnAdminAvailableUserRPProperties.filter(property => property.categoryID === category.id);

            return {
                category,
                categoryProperties
            };
        });

        return categoryPropertyArrays.filter(cpa => cpa.categoryProperties.length > 0);
    }, [
        axnAdminUserPropertyCategories,
        axnAdminAvailableUserRPProperties
    ]);

    return (
        <div className={styles.relyingPartyPermissionsContainer}>
            <Accordion defaultActiveKey={isExpandedByDefault ? "0" : null}>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>{relyingParty.name}</Accordion.Header>
                    <Accordion.Body>
                        {categorizedRPProperties.map(categoryObj => (
                            <CardContainer key={`rp-${relyingParty.id}-user-property-category-${categoryObj.category.id}`}>
                                <ContentPageHeader title={categoryObj.category.name} />
                                <ContentPageBody isMaxWidth={true}>
                                    <ContentPageBodyDescription descriptionText={categoryObj.category.description} />
                                    <div className={commonStyles.multiPermissionContainer}>
                                        {categoryObj.categoryProperties.map(property => (
                                            <RelyingPartyPermissionContainer
                                                key={`relying-party-${relyingParty.id}-permission-container-${property.shortName}`}
                                                axnAdminAvailableUserRPProperty={property}
                                                userId={userId}
                                                axnAdminUserRpProperties={axnAdminUserRpProperties}
                                                isDisabled={isDisabled || !canModify}
                                                relyingParty={relyingParty}
                                                addRelyingPartyProperties={addRelyingPartyProperties}
                                                removeRelyingPartyProperties={removeRelyingPartyProperties}
                                            />
                                        ))}
                                    </div>
                                </ContentPageBody>
                            </CardContainer>
                        ))}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </div>
    );
};